'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiCheckboxCircleLine,
  RiCoinsLine,
  RiFilmLine,
  RiLightbulbLine,
  RiShoppingBag3Line,
  RiShieldCheckLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { useInlineSign } from '@/i18n/direction';
import { dur, ease, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './creative.css';

/**
 * "Dört adım" — Ürün → Fikir → Taslak → Hazır.
 *
 * Akış ürünün kendisi (KREATIF-STUDYO-PLANI.md §2.2/§4): görünen akış SADE,
 * sahne/lens/kamera ayrıntısı yalnız "Ben yöneteyim" modunda. Her adımın
 * panelinde iki şey: o adımda NE oluyor ve kararı KİM veriyor (AI mı siz mi).
 * Fiyat Fikir adımında, onaydan ÖNCE — ürünün asıl sözü bu.
 *
 * Paneldeki ürün kartı vitrin örneğinin aynısı (Kynure serum); fikir, taslak
 * ve fiyat rakamları örnek — not satırı söylüyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * • Adımlar arası hat seçilen adıma kadar doluyor (scaleX) — "buraya kadar
 *   geldiniz".
 * • Panel yön biliyor: ileri giderken sağdan, geri dönerken soldan giriyor
 *   (`custom` = yön). Yön sağdan sola dilde ters (`useInlineSign`).
 * • Otomatik ilerleme YOK: adımı ziyaretçi seçiyor (DESIGN.md §4, sakinlik).
 */

const STEPS = ['product', 'idea', 'draft', 'ready'] as const;
type Step = (typeof STEPS)[number];

const STEP_ICON: Record<Step, typeof RiFilmLine> = {
  product: RiShoppingBag3Line,
  idea: RiLightbulbLine,
  draft: RiFilmLine,
  ready: RiCheckboxCircleLine,
};

const RATIOS = ['9:16', '4:5', '1:1', '16:9'] as const;
const MODES = ['ask', 'decide', 'direct'] as const;
const SHOTS = ['s1', 's2', 's3'] as const;
const DELIVER = ['d1', 'd2', 'd3', 'd4'] as const;

const panelV: Variants = {
  enter: (dir: number) => ({ opacity: 0, x: 24 * dir, filter: 'blur(4px)' }),
  show: { opacity: 1, x: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  leave: (dir: number) => ({
    opacity: 0,
    x: -16 * dir,
    filter: 'blur(3px)',
    transition: { duration: dur.instant },
  }),
};

export function CreativeFlow() {
  const t = useTranslations('featuresCreative.flow');
  const uid = useId();
  const sign = useInlineSign();
  const [[index, dir], setState] = useState<[number, number]>([0, 1]);
  const step = STEPS[index]!;

  const go = (next: number) => {
    if (next === index) return;
    setState([next, next > index ? 1 : -1]);
  };

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="crf">
        <ol className="crf-steps" aria-label={t('stepsLabel')}>
          {STEPS.map((key, i) => {
            const Icon = STEP_ICON[key];
            return (
              <li key={key} className="crf-step-item">
                {i > 0 && (
                  <span className="crf-link" aria-hidden="true">
                    <motion.span
                      className="crf-link-fill"
                      initial={false}
                      animate={{ scaleX: i <= index ? 1 : 0 }}
                      transition={{ duration: dur.base, ease: ease.out }}
                    />
                  </span>
                )}
                <button
                  type="button"
                  className="crf-step"
                  aria-pressed={i === index}
                  aria-controls={`${uid}-panel`}
                  data-done={i < index ? '' : undefined}
                  onClick={() => go(i)}
                >
                  <span className="crf-step-icon">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span className="crf-step-num">{i + 1}</span>
                  <span className="crf-step-name">{t(`steps.${key}.name`)}</span>
                  <span className="crf-step-who">{t(`steps.${key}.who`)}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="crf-stage" id={`${uid}-panel`} aria-live="polite">
          <AnimatePresence mode="wait" initial={false} custom={dir * sign}>
            <motion.div
              key={step}
              className="crf-panel"
              custom={dir * sign}
              variants={panelV}
              initial="enter"
              animate="show"
              exit="leave"
            >
              <div className="crf-copy">
                <p className="crf-panel-name">{t(`steps.${step}.name`)}</p>
                <p className="crf-panel-text">{t(`steps.${step}.text`)}</p>
              </div>

              <div className="crf-visual">
                {step === 'product' && (
                  <div className="crf-product">
                    <div className="crf-product-card">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        className="crf-product-img"
                        src="/samples/video/product-input.jpg"
                        alt=""
                        width={520}
                        height={520}
                        loading="lazy"
                        decoding="async"
                      />
                      <div>
                        <p className="crf-product-name">{t('product.name')}</p>
                        <p className="crf-product-src">{t('product.source')}</p>
                      </div>
                    </div>
                    <p className="crf-mini-label">{t('product.ratioLabel')}</p>
                    <ul className="crf-ratios">
                      {RATIOS.map((ratio, i) => (
                        <li
                          key={ratio}
                          className={`crf-ratio${i === 0 ? ' crf-ratio--on' : ''}`}
                          style={{ aspectRatio: ratio.replace(':', ' / ') }}
                        >
                          {ratio}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {step === 'idea' && (
                  <div className="crf-idea">
                    <ul className="crf-modes">
                      {MODES.map((mode, i) => (
                        <li key={mode} className={`crf-mode${i === 1 ? ' crf-mode--on' : ''}`}>
                          {t(`idea.modes.${mode}`)}
                        </li>
                      ))}
                    </ul>
                    <div className="crf-concept">
                      <p className="crf-concept-tag">{t('idea.tag')}</p>
                      <p className="crf-concept-title">{t('idea.title')}</p>
                      <p className="crf-concept-why">{t('idea.why')}</p>
                      <div className="crf-concept-foot">
                        <span className="crf-price">
                          <RiCoinsLine size={15} aria-hidden="true" />
                          {t('idea.price')}
                        </span>
                        <span className="crf-btn">{t('idea.approve')}</span>
                      </div>
                    </div>
                  </div>
                )}

                {step === 'draft' && (
                  <ol className="crf-shots">
                    {SHOTS.map((shot) => (
                      <li key={shot} className="crf-shot">
                        <span className="crf-shot-time">{t(`draft.${shot}.time`)}</span>
                        <p className="crf-shot-text">{t(`draft.${shot}.text`)}</p>
                      </li>
                    ))}
                  </ol>
                )}

                {step === 'ready' && (
                  <div className="crf-ready">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      className="crf-ready-poster"
                      src="/samples/video/product-poster.jpg"
                      alt=""
                      width={360}
                      height={640}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="crf-ready-copy">
                      <p className="crf-qa">
                        <RiShieldCheckLine size={16} aria-hidden="true" />
                        {t('ready.qa')}
                      </p>
                      <ul className="crf-deliver">
                        {DELIVER.map((item) => (
                          <li key={item}>{t(`ready.${item}`)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <p className="ps-note">{t('note')}</p>
    </ProductSection>
  );
}
